import React from 'react'
import { toast } from 'react-toastify'
import { Parser } from '@json2csv/plainjs'
import { Box, Grid, MenuItem, Select, TextField } from '@mui/material'
import Button from '@mui/material/Button'
import { styled } from '@mui/material/styles'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { DesktopDatePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useMutation } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import RewardsAPI from 'api/faber/RewardsAPI'
import { Form, Formik } from 'formik'
import OrbitSession from 'hooks/useCookies'
import * as Yup from 'yup'

import NormalText from '../../../../common/Typography/NormalText'

const CustomDatePicker = (props: DatePickerProps) => {
  const { label, disabled, date, setDate } = props

  const handleStartDate = (newValue: Date | null) => {
    setDate(newValue)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DesktopDatePicker
        label={label}
        inputFormat='dd/MM/yyyy'
        value={date}
        onChange={handleStartDate}
        renderInput={(params) => (
          <TextField placeholder='Date' variant='standard' {...params} />
        )}
        disabled={disabled}
      />
    </LocalizationProvider>
  )
}

export default CustomDatePicker

interface DatePickerProps {
  label: string
  disabled: boolean
  date: Date | null
  setDate: (value: Date | null) => void
}

const formikInitialValues = {
  distributorId: '',
  promocodeSummary: '',
  rewardProductId: '',
  maxClaims: 2,
  maxConsumption: 1,
  validityStartAt: 0,
  validityEndAt: 0,
  batchSize: 0
}

const NewDistributorPromoSchema = Yup.object().shape({
  distributorId: Yup.string().required('Distributor ID is required'),
  promocodeSummary: Yup.string().required(
    'Distributor promo summary is required'
  ),
  rewardProductId: Yup.string().required('Reward product ID is required'),
  maxClaims: Yup.number()
    .min(1, 'Max claims must be at least 1')
    .required('Max claims is required'),
  maxConsumption: Yup.number()
    .min(1, 'Max consumption must be at least 1')
    .required('Max consumption is required'),
  batchSize: Yup.number()
    .min(1, 'Batch size must be at least 1')
    .max(1000, 'Batch size must be at most 1000')
    .required('Batch size is required')
})

const CenteredBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: '80%'
})
const CenteredGrid = styled(Grid)({
  display: 'flex',
  alignItems: 'center'
})

const StyledTextField = styled(TextField)({
  '& input': {
    padding: '6px'
  },
  width: '100%'
})

export const NewDistributorPromoForm = ({
  handleClose
}: {
  handleClose: () => void
}) => {
  const [validityStartAt, setValidityStart] = React.useState<Date | null>(
    new Date()
  )
  const [validityEndAt, setValidityEnd] = React.useState<Date | null>(
    new Date()
  )

  const faberToken = OrbitSession.get('faberToken')
  const { data: distributors } = useQuery(['getDistributors'], async () =>
    RewardsAPI.getDistributors(faberToken)
  )

  const downloadCSV = (jsonData) => {
    const json2csvParser = new Parser()
    const csvData = json2csvParser.parse(jsonData)
    const blob = new Blob([csvData], { type: 'text/csv' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = 'data.csv' // Set the file name
    link.click()
  }

  const { mutate: createDistributorPromoCode } = useMutation(
    RewardsAPI.createDistributorPromo,
    {
      onSuccess: (res) => {
        downloadCSV(res.data.data)
        toast.success('Distributor code created successfully')
        handleClose()
      },
      onError: () => {
        toast.error('Error creating promo code')
      }
    }
  )

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleFormSubmit = (values: NewDistributorPromoFormikProps) => {
    const allValues = {
      distributorId: values.distributorId,
      promocodeSummary: values.promocodeSummary,
      rewardProductId: values.rewardProductId,
      maxClaims: values.maxClaims,
      maxConsumption: values.maxConsumption,
      validityStartAt: values.validityStartAt,
      validityEndAt: values.validityEndAt,
      batchSize: values.batchSize,
      faberToken: faberToken
    }
    createDistributorPromoCode(allValues)
  }

  /**
   * Event handlers
   */
  const handleUsageTypeSelect = (
    e,
    setFieldValue: (
      field: string,
      value,
      shouldValidate?: boolean | undefined
    ) => void
  ) => {
    setFieldValue(e.target.name, e.target.value)
  }

  return (
    <Formik
      initialValues={formikInitialValues}
      onSubmit={handleFormSubmit}
      validationSchema={NewDistributorPromoSchema}
    >
      {({
        submitForm,
        handleChange,
        errors,
        touched,
        handleBlur,
        setFieldValue
      }) => (
        <Form>
          <Grid container rowSpacing={2}>
            <Grid item xs={3}>
              <NormalText isBold>Distributor ID</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Select
                defaultValue=''
                variant='standard'
                sx={{ width: '80%' }}
                onChange={(e) => handleUsageTypeSelect(e, setFieldValue)}
                name='distributorId'
                label=''
              >
                {distributors &&
                  distributors.map((distributor) => (
                    <MenuItem key={distributor.id} value={distributor.id}>
                      {distributor.distributorName}
                    </MenuItem>
                  ))}
              </Select>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Distributor Promo Summary</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='promocodeSummary'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'promocode-summary'
                  }}
                  fullWidth
                  variant='outlined'
                  error={
                    !!(errors.promocodeSummary && touched.promocodeSummary)
                  }
                  helperText={
                    errors.promocodeSummary && touched.promocodeSummary
                      ? errors.promocodeSummary
                      : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Max Claims</NormalText>
            </Grid>
            <CenteredGrid item xs={9}>
              <CenteredBox>
                <StyledTextField
                  name='maxClaims'
                  type='number'
                  inputProps={{
                    min: 1
                  }}
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  error={!!(errors.maxClaims && touched.maxClaims)}
                  helperText={
                    errors.maxClaims && touched.maxClaims
                      ? errors.maxClaims
                      : null
                  }
                />
              </CenteredBox>
            </CenteredGrid>
            <Grid item xs={3}>
              <NormalText isBold>Max Consumption</NormalText>
            </Grid>
            <CenteredGrid item xs={9}>
              <CenteredBox>
                <StyledTextField
                  name='maxConsumption'
                  type='number'
                  inputProps={{
                    min: 1
                  }}
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  error={!!(errors.maxConsumption && touched.maxConsumption)}
                  helperText={
                    errors.maxConsumption && touched.maxConsumption
                      ? errors.maxConsumption
                      : null
                  }
                />
              </CenteredBox>
            </CenteredGrid>
            <Grid item xs={3}>
              <NormalText isBold>Validity Start Date</NormalText>
            </Grid>
            <Grid item xs={9}>
              <CustomDatePicker
                label='Validity Start Date'
                date={validityStartAt}
                setDate={(value: Date | null) => {
                  setValidityStart(value)
                  // Convert the Date object to Unix time
                  const unixTime = value ? Math.floor(value.getTime()) : null
                  setFieldValue('validityStartAt', unixTime)
                }}
                disabled={false}
              />
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Validity End Date</NormalText>
            </Grid>
            <Grid item xs={9}>
              <CustomDatePicker
                label='Validity End Date'
                date={validityEndAt}
                setDate={(value: Date | null) => {
                  setValidityEnd(value)
                  // Convert the Date object to Unix time
                  const unixTime = value ? Math.floor(value.getTime()) : null
                  setFieldValue('validityEndAt', unixTime)
                }}
                disabled={false}
              />
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Batch Size</NormalText>
            </Grid>
            <CenteredGrid item xs={9}>
              <CenteredBox>
                <StyledTextField
                  name='batchSize'
                  type='number'
                  inputProps={{
                    min: 0,
                    max: 1000
                  }}
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  error={!!(errors.batchSize && touched.batchSize)}
                  helperText={
                    errors.batchSize && touched.batchSize
                      ? errors.batchSize
                      : null
                  }
                />
              </CenteredBox>
            </CenteredGrid>
          </Grid>
          <Grid>
            <Box mt={3}>
              {' '}
              {/* Adjust the number to increase or decrease the space */}
              <Button autoFocus onClick={submitForm}>
                Save changes
              </Button>
            </Box>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}
interface NewDistributorPromoFormikProps {
  distributorId: string
  promocodeSummary: string
  rewardProductId: string
  maxClaims: number
  maxConsumption: number
  validityStartAt: number
  validityEndAt: number
  batchSize: number
}
